import React, { useState, useEffect } from 'react';

import moment from 'moment';
import { Link } from 'react-router-dom';

import Select from '../../../../../Form/Select';
import TextInput from '../../../../../Form/TextInput';
import CurrencyInput from '../../../../../Form/CurrencyInput';
import Checkbox from '../../../../../Form/Checkbox';
import Loader from '../../../../../Loader';
import BillComId from '../../../../FormContainer/BillComId';
import BankAccountField from './BankAccountField';
import Alert from '../../../../../Alert';

import useUserRole from '../../../../../../hooks/useUserRole';
import calculateAmountUnapplied from '../helpers/calculateAmountUnapplied';

let referenceTimer;
let amountTimer;

export default function PaymentForm({
  payment,
  isPaymentApplied,
  distributePayment,
  disabled,
  updatePayment,
  isPaymentVoid,
  isPending,
  isFetching,
  isPaymentUnapplied,
  source,
  isPaymentPaid,
  loan,
  readOnly,
}) {
  const [isCheckCleared, setIsCheckCleared] = useState(false);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [payer, setPayer] = useState('');
  const [isPayoff, setIsPayoff] = useState('');
  const [payToLienHolder, setPayToLienHolder] = useState('');
  const [isDistributed, setIsDistributed] = useState(false);

  const { isSuperAdmin } = useUserRole();

  useEffect(() => {
    setIsCheckCleared(payment?.check_cleared);
  }, [payment?.check_cleared]);

  useEffect(() => {
    setAmount(payment?.amount);
  }, [payment?.amount]);

  useEffect(() => {
    setReference(payment?.reference);
  }, [payment?.reference]);

  useEffect(() => {
    setPayer(payment?.payer);
  }, [payment?.payer]);

  useEffect(() => {
    setIsPayoff(payment?.is_payoff);
  }, [payment?.is_payoff]);

  useEffect(() => {
    setPayToLienHolder(payment?.pay_to_lien_holder);
  }, [payment?.pay_to_lien_holder]);

  useEffect(() => {
    setIsDistributed(payment?.distributed);
  }, [payment?.distributed]);

  // Update payment amount on change and after 1 second of inactivity
  useEffect(() => {
    clearTimeout(referenceTimer);
    referenceTimer = setTimeout(() => {
      if (reference === payment?.reference) {
        return;
      }

      updatePayment({
        ...payment,
        reference,
      });
    }, 1000);

    return () => clearTimeout(referenceTimer);
  }, [payment, reference, updatePayment]);

  useEffect(() => {
    if (!amount) {
      return;
    }

    clearTimeout(amountTimer);

    amountTimer = setTimeout(() => {
      if (`${amount}`.includes('_')) {
        return;
      }

      const formattedAmount = `${amount}`.replaceAll(',', '');

      if (parseFloat(formattedAmount, 2) === payment?.amount) {
        return;
      }

      updatePayment({
        ...payment,
        amount: formattedAmount,
      });
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(amountTimer);
  }, [amount, payment, payment?.amount, updatePayment]);

  return (
    <>
      <fieldset className="mb-10">
        <legend className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6">
          Payment
        </legend>
        {payment?._billcom_settlement && isSuperAdmin && (
          <div className="mb-4">
            <Alert
              message={
                <>
                  This payment settled on{' '}
                  {payment?._billcom_settlement?.date
                    ? moment(payment?._billcom_settlement.date).format('M/D/YY')
                    : ''}
                  .{' '}
                  <Link
                    to={`/billcom-settlements/${payment._billcom_settlement.billcom_settlement_parent_id}`}
                    className="underline"
                  >
                    View details
                  </Link>
                </>
              }
            />
          </div>
        )}
        <div className="grid gap-5 md:grid-cols-3 xl:grid-cols-4">
          <Select
            readOnly={isPaymentVoid || isPaymentApplied || readOnly}
            label="Payer"
            value={payer}
            setFieldValue={(name, value) => {
              setPayer(value);
              updatePayment({
                ...payment,
                payer: value,
              });
            }}
            options={[
              { label: 'Borrower', value: 'Borrower' },
              { label: 'Lender', value: 'Lender' },
              { label: 'Servicer', value: 'Servicer' },
              { label: 'Vendor', value: 'Vendor' },
            ]}
          />
          <TextInput
            label="Date"
            value={
              payment?.date ? moment(payment.date).format('MMM D, YYYY') : ''
            }
            readOnly
          />
          <TextInput label="Source" value={payment?.source} readOnly />
          <BankAccountField
            isPaymentVoid={isPaymentVoid}
            isPaymentApplied={isPaymentApplied}
            readOnly={readOnly}
            updatePayment={updatePayment}
            payment={payment}
          />
          <TextInput
            label="Reference"
            value={reference}
            readOnly={
              !(isPaymentUnapplied && source === 'Check') ||
              isPaymentVoid ||
              isPaymentApplied ||
              readOnly
            }
            onChange={(event) => setReference(event.target.value)}
          />
          <CurrencyInput
            label="Amount"
            value={amount}
            readOnly={
              !isPaymentUnapplied ||
              isPaymentVoid ||
              isPaymentApplied ||
              readOnly
            }
            onChange={(event) => setAmount(event.target.value)}
          />
          <CurrencyInput
            label="Amount unapplied"
            value={calculateAmountUnapplied(payment)}
            readOnly
          />
          {payment?.source !== 'ACH' && (
            <Checkbox
              disabled={isPaymentVoid || readOnly || isDistributed}
              readOnly={isPaymentVoid || readOnly}
              label={
                payment?.source === 'ACH direct'
                  ? 'ACH Cleared'
                  : 'Check Cleared'
              }
              value={isCheckCleared}
              onChange={() => {
                updatePayment({
                  ...payment,
                  check_cleared: !isCheckCleared,
                });
                setIsCheckCleared((prevState) => !prevState);
              }}
            />
          )}
          {isPaymentVoid && (
            <Select
              readOnly={readOnly}
              label="Status"
              value={payment?.status}
              setFieldValue={(name, value) => {
                updatePayment({
                  ...payment,
                  status: value,
                });
              }}
              options={[
                { label: 'Void', value: 'void' },
                { label: 'Unapplied', value: 'unapplied' },
              ]}
            />
          )}
          <TextInput
            label="Confirmation Number"
            value={payment?.confirmation_number}
            readOnly
          />
          {isPaymentVoid || isPaymentApplied || readOnly ? (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {isPayoff ? (
                <TextInput readOnly label="Payoff?" value="Yes" />
              ) : null}
            </>
          ) : (
            <Checkbox
              disabled={
                isFetching ||
                isPaymentPaid ||
                isPaymentVoid ||
                isPaymentApplied ||
                readOnly ||
                payer === 'Vendor'
              }
              label="Payoff?"
              value={isPayoff}
              onChange={() => {
                updatePayment({
                  ...payment,
                  is_payoff: !isPayoff,
                });
                setIsPayoff((prevState) => !prevState);
              }}
            />
          )}
          {!!loan?.lien_holder_id && (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {isFetching ||
              isPaymentPaid ||
              isPaymentApplied ||
              isPaymentVoid ||
              readOnly ? (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {payToLienHolder ? (
                    <TextInput
                      label="Pay to lien holder"
                      value="Yes"
                      readOnly
                    />
                  ) : null}
                </>
              ) : (
                <Checkbox
                  disabled={
                    isFetching ||
                    isPaymentPaid ||
                    isPaymentApplied ||
                    isPaymentVoid ||
                    readOnly
                  }
                  label="Pay to lien holder"
                  value={payToLienHolder}
                  onChange={() => {
                    updatePayment({
                      ...payment,
                      pay_to_lien_holder: !payToLienHolder,
                    });
                    setPayToLienHolder((prevState) => !prevState);
                  }}
                />
              )}
            </>
          )}
          <Checkbox
            disabled
            label="Distributed"
            value={isDistributed}
            onChange={() => {
              updatePayment({
                ...payment,
                distributed: !isDistributed,
              });
              setIsDistributed((prevState) => !prevState);
            }}
          />
        </div>
      </fieldset>
      {!readOnly && (
        <BillComId
          form={{ values: { billcom_id: payment?.billcom_receivedpay_id } }}
        />
      )}
      {!disabled && !readOnly && (
        <button
          disabled={isPending || isFetching}
          type="submit"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full flex justify-center"
          onClick={distributePayment}
        >
          {isPending ? <Loader /> : 'Apply Payment'}
        </button>
      )}
    </>
  );
}
